import type { CameraMatrices } from './camera'
import { checkedShader } from './device'
import { environmentShader } from './environment-shaders'

export interface EnvironmentPassConfig {
  preset: 'ocean' | 'space' | 'simple'
  /** Presentation time in seconds; keep fixed to disable ambient motion. */
  time: number
  center: readonly [number, number, number]
  tableSize: readonly [number, number]
}

export interface EnvironmentPassTargets {
  depthView: GPUTextureView
  shadowView: GPUTextureView
}

/**
 * A world-oriented backdrop, independent of components and game state. It draws
 * into the renderer's existing multisampled color attachment; the following
 * surface pass loads that attachment and resolves it to the canvas.
 */
export class EnvironmentPass {
  private readonly buffer: GPUBuffer
  private readonly sampler: GPUSampler
  private binding?: GPUBindGroup
  private shadowView?: GPUTextureView
  private readonly uniforms = new Float32Array(64)
  private disposed = false

  private constructor(private readonly device: GPUDevice, private readonly pipeline: GPURenderPipeline) {
    this.buffer = device.createBuffer({
      label: 'Environment camera and presentation', size: this.uniforms.byteLength,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    })
    this.sampler = device.createSampler({ label: 'Environment shadow comparison', compare: 'less-equal', magFilter: 'linear', minFilter: 'linear' })
  }

  static async create(device: GPUDevice, format: GPUTextureFormat): Promise<EnvironmentPass> {
    const module = await checkedShader(device, environmentShader, 'Table environments')
    const pipeline = await device.createRenderPipelineAsync({
      label: 'World-oriented environments · 4× MSAA', layout: 'auto',
      vertex: { module, entryPoint: 'environmentVertex' },
      fragment: { module, entryPoint: 'environmentFragment', targets: [{ format }] },
      primitive: { topology: 'triangle-list' }, multisample: { count: 4 },
      depthStencil: { format: 'depth24plus', depthWriteEnabled: true, depthCompare: 'always' },
    })
    return new EnvironmentPass(device, pipeline)
  }

  draw(
    encoder: GPUCommandEncoder,
    targetView: GPUTextureView,
    camera: Pick<CameraMatrices, 'inverseViewProjection' | 'viewProjection' | 'lightViewProjection' | 'eye'>,
    width: number,
    height: number,
    config: EnvironmentPassConfig,
    targets: EnvironmentPassTargets,
  ): void {
    if (this.disposed) return
    if (!this.binding || this.shadowView !== targets.shadowView) {
      this.shadowView = targets.shadowView
      this.binding = this.device.createBindGroup({
        label: 'Environment presentation and shadows', layout: this.pipeline.getBindGroupLayout(0),
        entries: [
          { binding: 0, resource: { buffer: this.buffer } },
          { binding: 1, resource: targets.shadowView },
          { binding: 2, resource: this.sampler },
        ],
      })
    }
    this.uniforms.set(camera.inverseViewProjection, 0)
    this.uniforms.set([...camera.eye, Number.isFinite(config.time) ? config.time : 0], 16)
    this.uniforms.set([Math.max(1, width), Math.max(1, height), config.preset === 'ocean' ? 1 : config.preset === 'space' ? 2 : 0, 0], 20)
    this.uniforms.set([...config.center, config.center[1] - 0.72], 24)
    this.uniforms.set([Math.max(1, config.tableSize[0]), Math.max(1, config.tableSize[1]), 0, 0], 28)
    this.uniforms.set(camera.viewProjection, 32)
    this.uniforms.set(camera.lightViewProjection, 48)
    this.device.queue.writeBuffer(this.buffer, 0, this.uniforms.buffer)
    const pass = encoder.beginRenderPass({
      label: 'Environment backdrop',
      colorAttachments: [{ view: targetView, loadOp: 'clear', storeOp: 'store', clearValue: { r: 0.035, g: 0.045, b: 0.05, a: 1 } }],
      depthStencilAttachment: { view: targets.depthView, depthLoadOp: 'clear', depthClearValue: 1, depthStoreOp: 'store' },
    })
    pass.setPipeline(this.pipeline)
    pass.setBindGroup(0, this.binding)
    pass.draw(3)
    pass.end()
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    this.buffer.destroy()
    this.binding = undefined
    this.shadowView = undefined
  }
}
